import { useState } from "react";
import {
    Box, TextField, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, CircularProgress, Typography,
    Stack, FormControl, InputLabel, Select, MenuItem, Tooltip
} from "@mui/material";
import { useSelector } from "react-redux";
import { useLazyGetSentUniversityCourseLogsQuery } from "../../store/services/logs-service";

const SentUniversityCoursesLog = () => {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [filters, setFilters] = useState({
        userId: '',
        universityCourseName: '',
        dateFrom: '',
        dateTo: '',
        dateCompleted: '',
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [trigger, { data, isLoading, error }] = useLazyGetSentUniversityCourseLogsQuery();

    const handleSearch = (newPage = 0) => {
        setPage(newPage);
        trigger({
            tenancyId,
            ...filters,
            page: newPage,
            pageSize,
        });
    };

    const handlePageChange = (event, newPage) => {
        handleSearch(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        setPage(0);
        trigger({
            tenancyId,
            ...filters,
            page: 0,
            pageSize: newSize,
        });
    };

    const columns = [
        { id: 'Id', label: 'ID', width: 60 },
        { id: 'DateCreated', label: 'Date Created', width: 160 },
        { id: 'DateUpdated', label: 'Date Updated', width: 160 },
        { id: 'DateSent', label: 'Date Sent', width: 160 },
        { id: 'UserId', label: 'User ID', width: 200 },
        { id: 'UniversityCourseId', label: 'Course ID', width: 180 },
        { id: 'UniversityCourseName', label: 'Course Name', width: 200 },
        { id: 'DateCompleted', label: 'Date Completed', width: 160 },
        { id: 'CorrectAnswers', label: 'Correct', width: 80 },

        { id: 'UniqueIdentifier', label: 'Unique ID', width: 180 },
    ];

    const formatDate = (val) => {
        if (!val) return '-';
        try {
            return new Date(val).toLocaleString();
        } catch {
            return val;
        }
    };

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small"
                    label="User ID / Email"
                    value={filters.userId}
                    onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Course Name"
                    value={filters.universityCourseName}
                    onChange={(e) => setFilters({ ...filters, universityCourseName: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Date From"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
                <TextField
                    size="small"
                    label="Date To"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Completed Status</InputLabel>
                    <Select
                        value={filters.dateCompleted}
                        label="Completed Status"
                        onChange={(e) => setFilters({ ...filters, dateCompleted: e.target.value })}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Completed Only</MenuItem>
                        <MenuItem value="false">Not Completed</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={() => handleSearch(0)}>
                    Search
                </Button>
            </Stack>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Typography color="error" sx={{ p: 2 }}>
                    Error loading data: {error.status || 'Unknown error'}
                </Typography>
            )}

            {data && (
                <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Total results: {data.total}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell key={col.id} sx={{ fontWeight: 600, minWidth: col.width }}>
                                            {col.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.results && data.results.map((row, idx) => (
                                    <TableRow key={row.Id || idx} hover>
                                        <TableCell>{row.Id}</TableCell>
                                        <TableCell>{formatDate(row.DateCreated)}</TableCell>
                                        <TableCell>{formatDate(row.DateUpdated)}</TableCell>
                                        <TableCell>{formatDate(row.DateSent)}</TableCell>
                                        <TableCell>{row.UserId || '-'}</TableCell>
                                        <TableCell>{row.UniversityCourseId || '-'}</TableCell>
                                        <TableCell>{row.UniversityCourseName || '-'}</TableCell>
                                        <TableCell>{formatDate(row.DateCompleted)}</TableCell>
                                        <TableCell>{row.CorrectAnswers ?? '-'}</TableCell>

                                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.UniqueIdentifier || '-'}</TableCell>
                                    </TableRow>
                                ))}
                                {data.results && data.results.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            No results found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={data.total || 0}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                    />
                </>
            )}

            {!data && !isLoading && !error && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Click Search to view most recent <Tooltip title="Records of training courses that have been sent to users, including recipient details, course name, and delivery status." arrow placement="bottom"><span style={{ textDecoration: "underline dotted", cursor: "help", color: "inherit" }}>sent course logs</span></Tooltip> or enter search criteria to filter results.
                </Typography>
            )}
        </Box>
    );
};

export default SentUniversityCoursesLog;
